import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box, Divider, Popover } from '@mui/material'
import type { ReactNode } from 'react'

export type TopbarMenuItemConfig = {
  color?: string
  disabled?: boolean
  endAdornment?: ReactNode
  hasSubmenu?: boolean
  id: string
  label: ReactNode
  onClick?: () => void
  shortcut?: ReactNode
}

export type TopbarMenuSection = {
  items: TopbarMenuItemConfig[]
}

export type TopbarMenuHeader = {
  avatar?: ReactNode
  name?: ReactNode
  statusColor?: string
  subtitle?: ReactNode
}

export type TopbarMenuProps = {
  anchorEl?: Element | null
  header?: TopbarMenuHeader
  onClose: () => void
  open: boolean
  sections: TopbarMenuSection[]
  width?: number | string
}

export function TopbarMenu({ anchorEl, header, onClose, open, sections, width = 240 }: TopbarMenuProps) {
  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      onClose={onClose}
      open={open}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 1.5,
            boxShadow: '0 0.5rem 1rem rgba(0,0,0,0.15)',
            mt: 0.5,
            overflow: 'hidden',
            width,
          },
        },
      }}
    >
      {header != null && (
        <>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, px: 2.5, py: 2 }}>
            {header.avatar != null && (
              <Box sx={{ flexShrink: 0, position: 'relative' }}>
                {header.avatar}
                {header.statusColor != null && (
                  <Box
                    sx={{
                      backgroundColor: header.statusColor,
                      border: '2px solid',
                      borderColor: 'background.paper',
                      borderRadius: '50%',
                      bottom: 0,
                      height: 12,
                      position: 'absolute',
                      right: 0,
                      width: 12,
                    }}
                  />
                )}
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              {header.name != null && (
                <Box sx={{ fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.3 }}>{header.name}</Box>
              )}
              {header.subtitle != null && (
                <Box sx={{ color: 'text.secondary', fontSize: '0.8125rem', mt: 0.25 }}>{header.subtitle}</Box>
              )}
            </Box>
          </Box>
          <Divider />
        </>
      )}
      {sections.map((section, sectionIndex) => (
        <Box key={sectionIndex}>
          {section.items.map(item => (
            <Box
              key={item.id}
              component="button"
              disabled={item.disabled}
              onClick={() => {
                item.onClick?.()
                onClose()
              }}
              sx={{
                alignItems: 'center',
                background: 'none',
                border: 'none',
                color: item.color ?? 'text.primary',
                cursor: item.disabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                fontSize: '0.9375rem',
                justifyContent: 'space-between',
                opacity: item.disabled ? 0.5 : 1,
                px: 2.5,
                py: 1.5,
                textAlign: 'left',
                transition: 'background-color 150ms ease',
                width: '100%',
                '&:hover:not(:disabled)': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Box component="span">{item.label}</Box>
              <Box sx={{ alignItems: 'center', display: 'flex', flexShrink: 0, gap: 0.5 }}>
                {item.shortcut != null && renderShortcut(item.shortcut)}
                {item.endAdornment != null && (
                  <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                    {item.endAdornment}
                  </Box>
                )}
                {item.hasSubmenu === true && <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: 16 }} />}
              </Box>
            </Box>
          ))}
          {sectionIndex < sections.length - 1 && <Divider />}
        </Box>
      ))}
    </Popover>
  )
}

function renderShortcut(shortcut: ReactNode): ReactNode {
  if (typeof shortcut !== 'string') return shortcut

  const parts = shortcut.split(/\s*\+\s*/)
  const elements: ReactNode[] = []

  parts.forEach((key, index) => {
    if (index > 0) {
      elements.push(
        <Box key={`sep-${index}`} component="span" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          +
        </Box>,
      )
    }
    elements.push(
      <Box
        key={`key-${index}`}
        component="span"
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 0.75,
          color: 'text.secondary',
          fontFamily: 'monospace',
          fontSize: '0.6875rem',
          px: 0.75,
          py: 0.25,
        }}
      >
        {key}
      </Box>,
    )
  })

  return (
    <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
      {elements}
    </Box>
  )
}
